import type {
  SystemNotification,
  UpdateSystemNotificationsConfigRequest,
} from '@store/generated/admin';

export type EditableNotification = SystemNotification;
export type SaveNotification = UpdateSystemNotificationsConfigRequest['notifications'][number];
export type NotificationSeverity = EditableNotification['severity'];
export type NotificationType = EditableNotification['type'];
export type TargetingScope = EditableNotification['targeting']['scope'];

export interface ValidationResult {
  isValid: boolean;
  errorsByIndex: Record<number, string[]>;
  errors: string[];
}

export const NEW_NOTIFICATION: EditableNotification = {
  id: 'system-notification',
  type: 'banner',
  severity: 'info',
  title: '',
  message: '',
  dismissible: true,
  persistent: true,
  targeting: { scope: 'global' },
};

export const SEVERITIES: NotificationSeverity[] = ['info', 'warning', 'error'];
export const TYPES: NotificationType[] = ['banner', 'modal'];
export const TARGETING_SCOPES: TargetingScope[] = ['global', 'route'];

export function normalizeNotifications(
  notifications: EditableNotification[] = [],
): EditableNotification[] {
  return notifications.map(notification => ({
    ...notification,
    startsAt: notification.startsAt || undefined,
    expiresAt: notification.expiresAt || undefined,
    link: notification.link
      ? {
          url: notification.link.url || '',
          text: notification.link.text || '',
        }
      : undefined,
    targeting: {
      scope: notification.targeting.scope,
      routes:
        notification.targeting.scope === 'route'
          ? (notification.targeting.routes ?? []).filter(route => route.trim())
          : undefined,
    },
  }));
}

export function cleanForSave(notification: EditableNotification): SaveNotification {
  const cleaned: SaveNotification = {
    id: notification.id.trim(),
    type: notification.type,
    severity: notification.severity,
    title: notification.title.trim(),
    message: notification.message.trim(),
    dismissible: notification.dismissible,
    persistent: notification.persistent,
    targeting: {
      scope: notification.targeting.scope,
      routes:
        notification.targeting.scope === 'route'
          ? (notification.targeting.routes ?? []).map(route => route.trim()).filter(Boolean)
          : undefined,
    },
  };

  if (notification.link) {
    cleaned.link = {
      url: notification.link.url.trim(),
      text: notification.link.text.trim(),
    };
  }

  if (notification.startsAt) cleaned.startsAt = notification.startsAt;
  if (notification.expiresAt) cleaned.expiresAt = notification.expiresAt;

  return cleaned;
}

export function buildNotification(existingIds: Set<string>): EditableNotification {
  const id = nextUniqueId('system-notification', existingIds);
  return {
    ...NEW_NOTIFICATION,
    id,
    title: 'New notification',
    message: 'Notification message',
  };
}

export function nextUniqueId(baseId: string, existingIds: Set<string>): string {
  const normalizedBase = slugify(baseId) || 'system-notification';
  if (!existingIds.has(normalizedBase)) return normalizedBase;

  let index = 2;
  while (existingIds.has(`${normalizedBase}-${index}`)) {
    index += 1;
  }
  return `${normalizedBase}-${index}`;
}

export function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function validateNotifications(notifications: EditableNotification[]): ValidationResult {
  const errorsByIndex: Record<number, string[]> = {};
  const errors: string[] = [];
  const idCounts = new Map<string, number>();

  notifications.forEach(notification => {
    const id = notification.id.trim();
    if (!id) return;
    idCounts.set(id, (idCounts.get(id) ?? 0) + 1);
  });

  const addError = (index: number, message: string) => {
    errorsByIndex[index] = [...(errorsByIndex[index] ?? []), message];
    errors.push(message);
  };

  notifications.forEach((notification, index) => {
    const id = notification.id.trim();
    if (!id) addError(index, 'Notification ID is required.');
    if (id && (idCounts.get(id) ?? 0) > 1) addError(index, 'Notification ID must be unique.');
    if (!notification.title.trim()) addError(index, 'Title is required.');
    if (!notification.message.trim()) addError(index, 'Message is required.');

    if (notification.targeting.scope === 'route') {
      const routes = notification.targeting.routes?.filter(route => route.trim()) ?? [];
      if (!routes.length) addError(index, 'Route targeting requires at least one route.');
    }

    if (notification.startsAt && notification.expiresAt) {
      const startsAt = new Date(notification.startsAt).getTime();
      const expiresAt = new Date(notification.expiresAt).getTime();
      if (!Number.isNaN(startsAt) && !Number.isNaN(expiresAt) && startsAt >= expiresAt) {
        addError(index, 'Start time must be before expiration time.');
      }
    }

    if (notification.link) {
      const linkUrl = notification.link.url.trim();
      const linkText = notification.link.text.trim();
      if (!linkUrl || !linkText) addError(index, 'CTA link requires both URL and text.');
      if (linkUrl && !/^https?:\/\/\S+$/i.test(linkUrl)) {
        addError(index, 'CTA URL must be an absolute HTTP(S) URL.');
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errorsByIndex,
    errors,
  };
}

export function toDateTimeLocalValue(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const timezoneOffsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - timezoneOffsetMs).toISOString().slice(0, 16);
}

export function fromDateTimeLocalValue(value: string): string | undefined {
  if (!value) return undefined;
  return new Date(value).toISOString();
}

export function formatStatsigTimestamp(value?: number): string {
  if (!value) return 'Unknown';
  const milliseconds = value < 10_000_000_000 ? value * 1000 : value;
  return new Date(milliseconds).toLocaleString();
}

export function getMutationStatus(error: unknown): number | string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  if (!('status' in error)) return undefined;
  return (error as { status?: number | string }).status;
}
